import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const Dentures = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dentures Fort Worth TX | Full & Partial Dentures | Fort Worth Dental</title>
        <meta name="description" content="Quality dentures in Fort Worth TX. Full and partial dentures, implant-supported options. Natural-looking tooth replacement solutions. Call (817) 985-3337." />
        <meta name="keywords" content="dentures fort worth, partial dentures fort worth tx, full dentures, implant dentures, false teeth, removable dentures" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-3.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Dentures in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Complete and Partial Dentures for a Confident Smile</p>
            <p className="text-sm mt-4 opacity-90">Affordable Dentures | Custom Partial Dentures | Full Denture Replacement</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* When Do I Need Dentures */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/dentures-new-1.jpg" 
                  alt="Full and partial dentures Fort Worth TX - Custom removable teeth replacement for natural looking smile" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-3.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">When Do I Need Dentures?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A patient should consider the use of dentures as a possible solution when he is missing one or more teeth. Not only can dentures replace missing teeth, but they also provide support and structure for lips and cheeks that are around the missing teeth. The development of dental technology has produced modern dentures that are very natural in their look and feel. Replacing missing teeth with dentures can be a definite confidence booster. You can once again feel comfortable with smiling and eating in public.
                </p>
              </div>
            </div>
          </section>

          {/* What Types Of Dentures Are There */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">What Types Of Dentures Are There?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              There are two main types of dentures available for use.
            </p>
            
            <div className="space-y-8">
              {/* Partial Dentures */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Partial Dentures</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If a patient still retains some of his original teeth, he may find partial dentures to be helpful. Partial dentures will only replace those specific missing teeth. The denture is affixed to a plastic base and placed in the mouth. Some patients have these partial dentures cemented into place using his remaining natural teeth as anchor points. In this case, the dentures not only replace missing teeth, but they also provide support for the remaining natural teeth. This structural support keeps the natural teeth from shifting.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  When the partial denture is an upper denture, the attached base will usually be flesh-colored in order to blend in with the roof of the mouth. When the partial denture is a lower denture, the base will be manufactured to be horseshoe-shaped so that there will be comfortable room for the tongue.
                </p>
              </div>

              {/* Complete Dentures */}
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Complete Dentures</h3>
                <p className="text-gray-700 leading-relaxed">
                  Some patients may need complete dentures. These are needed when a patient has none of his original teeth remaining. This can happen through extraction or an accident that eliminates the natural teeth. Complete dentures replace every single tooth of the patient. Complete denture sets are custom fit and will produce a natural and radiant smile. Full dentures are made to be removed and will need the occasional adjustment by your dentist.
                </p>
              </div>
            </div>
          </section>

          {/* The Denture Process */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">What Is The Denture Process?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img 
                  src="/images/services/dentures-new-2.jpg" 
                  alt="Denture fitting process at Fort Worth Dental - Creating custom-fit removable prosthetics for comfortable wear" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
              <div>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Getting dentures typically requires several appointments over the course of a few weeks. Your Fort Worth dentist will:
                </p>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold text-lg">1.</span>
                    <span>Take precise impressions and measurements of your jaw</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold text-lg">2.</span>
                    <span>Create models, wax forms, and/or plastic patterns to determine the exact shape and position of the denture</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold text-lg">3.</span>
                    <span>Try in the denture several times to assess fit, color, and shape before the final denture is cast</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold text-lg">4.</span>
                    <span>Make any necessary adjustments after your final denture is placed</span>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Benefits of Dentures */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Dentures</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Restores ability to eat and speak properly</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves facial appearance and prevents sagging</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Custom-fitted for comfort and natural appearance</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Affordable tooth replacement option</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Can be adjusted and relined as needed</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Boosts confidence and self-esteem</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Modern dentures look very natural</span>
              </li>
            </ul>
          </section>

          {/* Caring for Your Dentures */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Caring for Your Dentures</h2>
            <div className="bg-teal-50 p-6 rounded-lg">
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                With proper care, your dentures can last for many years. Important care tips include:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Clean dentures daily with a denture brush and cleanser</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Soak dentures overnight in water or denture solution</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Rinse dentures before putting them back in your mouth</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Handle dentures carefully to avoid dropping them</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2">•</span>
                  <span>Visit your dentist regularly for check-ups and adjustments</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Dentures?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team creates custom dentures that look natural and fit comfortably.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Custom Fit</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Dentures Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about affordable dentures in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Dentures Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Dentures;